/**
 * Members management (WS-C) — roster, invites, pending-invitation revoke, and
 * per-member role changes + removal for the active workspace.
 *
 * Owner + Member model: only an owner sees the management controls (invite,
 * revoke, Edit); a member sees a read-only roster. The server enforces the
 * same boundary (Better Auth org access control) — this is the UI half. Invite
 * emails are sent server-side (sendInvitationEmail, apps/server/src/auth.ts) and
 * land on the public /accept-invitation/$invitationId route.
 *
 * Role changes and removal both live behind the Edit button, in
 * MemberEditDialog.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { SectionCard } from '@/components/patterns/section-card';
import { StatusPill } from '@/components/patterns/status-pill';
import { MemberEditDialog } from '@/components/settings/member-edit-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@/lib/auth-client';
import { qk } from '@/lib/query-client';
import { type OrgMember, useActiveOrg } from '@/lib/use-active-org';

export function MembersPanel() {
  const qc = useQueryClient();
  const { activeOrgId, meId, members, invitations, isOwner, isLoading } = useActiveOrg();

  const pending = invitations.filter((i) => i.status === 'pending');
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.org(activeOrgId) });

  const [inviteEmail, setInviteEmail] = useState('');
  const invite = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await authClient.organization.inviteMember({ email, role: 'member' });
      if (error) throw new Error(error.message ?? 'Invite failed');
    },
    onSuccess: () => {
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await authClient.organization.cancelInvitation({ invitationId });
      if (error) throw new Error(error.message ?? 'Could not revoke invitation');
    },
    onSuccess: () => {
      toast.success('Invitation revoked');
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The member being edited, if any. Held as the row's own object so the dialog
  // opens on that member's current role without another fetch.
  const [editing, setEditing] = useState<OrgMember | null>(null);

  return (
    <SectionCard title="Members" description="People with access to this workspace.">
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </div>
        ) : (
          <div className="divide-y divide-border-subtle">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-3">
                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                  {m.user.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {m.user.name}
                    {m.userId === meId ? ' (you)' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground">{m.user.email}</p>
                </div>
                <span className="text-xs font-semibold capitalize text-faint">{m.role}</span>
                {/* Owners may edit anyone but themselves — including another
                    owner, since the server refuses any change that would leave
                    the workspace ownerless. */}
                {isOwner && m.userId !== meId ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${m.user.email}`}
                    onClick={() => setEditing(m)}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 ? (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-faint">
                Pending invitations
              </p>
              {pending.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3">
                  <p className="min-w-0 flex-1 truncate text-sm">{inv.email}</p>
                  <StatusPill tone="neutral">Invited</StatusPill>
                  {isOwner ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(inv.id)}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        ) : null}

        {isOwner ? (
          <>
            <Separator />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const email = inviteEmail.trim();
                if (email) invite.mutate(email);
              }}
              className="flex gap-2"
            >
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <Button type="submit" size="sm" disabled={invite.isPending || !inviteEmail.trim()}>
                {invite.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Invite
                  </>
                )}
              </Button>
            </form>
          </>
        ) : null}

        {/* Keyed by member id so each opening remounts with that member's role. */}
        {editing ? (
          <MemberEditDialog
            key={editing.id}
            member={editing}
            onClose={() => setEditing(null)}
            onChanged={invalidate}
          />
        ) : null}
      </div>
    </SectionCard>
  );
}
